import { Feature, Geometry } from "geojson";
import mapboxgl, { CirclePaint, LinePaint } from "mapbox-gl";

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };

/**
 * Default style for snap preview circle indicator
 */
export const DEFAULT_SNAP_PREVIEW_STYLE: CirclePaint = {
  "circle-radius": 6,
  "circle-color": "#fbb03b",
  "circle-stroke-color": "#ffffff",
  "circle-stroke-width": 2,
  "circle-opacity": 0.8,
};

/**
 * Adds a temporary preview source + line layer to the map, used by the shortest_path_feature
 * draw mode to show which features the user is about to commit while a drag is in progress.
 */
export const ensurePreviewLayer = (map: mapboxgl.Map, sourceId: string, layerId: string, paint: LinePaint): void => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, { type: "geojson", data: EMPTY_FC });
  }
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      layout: { "line-cap": "round", "line-join": "round" },
      paint,
    });
  }
};

export const setPreviewFeatures = (map: mapboxgl.Map, sourceId: string, features: Array<Feature<Geometry>>): void => {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData({ type: "FeatureCollection", features });
};

export const removePreviewLayer = (map: mapboxgl.Map, sourceId: string, layerId: string): void => {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
};

/**
 * Adds a temporary preview source + circle layer to the map, used to show the snapped
 * graph node in shortest_path_feature mode. Features set via setPreviewFeatures should be
 * Point geometries.
 */
export const ensurePreviewCircleLayer = (map: mapboxgl.Map, sourceId: string, layerId: string, paint: CirclePaint): void => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, { type: "geojson", data: EMPTY_FC });
  }
  if (!map.getLayer(layerId)) {
    map.addLayer({ id: layerId, type: "circle", source: sourceId, paint });
  }
};
